import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyStateProps,
  FilterDropdown,
  Pagination,
  TabItem,
  Tabs,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
} from "../../../components/ui";
// The one screen-sized illustration on this page, and only for a roster with
// nothing in it at all — see `EmptyState`'s `welcome` variant.
import { ReactComponent as FirstUserMark } from "../../../assets/svg/No data images/my client.svg";
import UsersTable from "../components/UsersTable";
import type { RemindState } from "../components/UsersTable";
import useUsers from "../hooks/useUsers";
import { reactivateUser } from "../services/usersService";
import { ACCOUNT_TYPE_OPTIONS } from "../statusStyles";
import { userDetailPath } from "../../../routes/paths";
import type { AccountType, AdminUser, UserTabId } from "../types/users.types";

const TAB_COPY: Record<UserTabId, { label: string; cardTitle: string }> = {
  all: { label: "All", cardTitle: "Total users" },
  expired: { label: "Expired", cardTitle: "Total users" },
};

/** How long the box waits after the last keystroke before asking the server. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Every Naurist account, in one list.
 *
 * Two tabs and a dropdown, which divide the work between them: the tabs are
 * the state an account is in — everyone, or the ones that have lapsed — and
 * the dropdown is what they are paying for. They used to be one control, with
 * a tab per plan, and that left nowhere to ask about a lapsed account without
 * adding a fourth and fifth tab.
 *
 * Paging, searching and both filters happen on the server — see
 * `usersService` — so the browser never holds the whole roster.
 */
const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<UserTabId>("all");
  const [accountType, setAccountType] = useState<AccountType>("any");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Which lapsed accounts have been nudged, and how that went. Kept on the
  // page rather than in the table so it survives a re-fetch of the rows, and
  // deliberately not persisted: it is a note about this sitting, not a fact
  // about the account — the server is where that lives.
  const [reminders, setReminders] = useState<Record<number, RemindState>>({});
  const [remindError, setRemindError] = useState<string | null>(null);

  // What is typed and what has been asked for are two different things: a
  // request per keystroke is a request per keystroke, and the roster is paged
  // on the server. The box stays instant; the query follows it.
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [search]);

  const { users, counts, status, totalPages, reload } = useUsers({
    page,
    tab: activeTab,
    accountType,
    search: query,
  });

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(status === "loading");

  const handleTabChange = (tab: UserTabId) => {
    setActiveTab(tab);
    setSearch("");
    setQuery("");
    setPage(1);
    // Whatever went wrong sending a reminder went wrong on a tab that is no
    // longer on screen, and the rows it referred to are gone with it.
    setRemindError(null);
    // The plan filter is dropped on the way across, because the Expired tab
    // does not offer it: carrying a hidden "Yearly" over would leave somebody
    // looking at two rows out of a list they were told held eight, with no
    // control on screen to explain it.
    setAccountType("any");
  };

  /**
   * The Expired tab's one action: mail a lapsed account an invitation back.
   *
   * The row settles into "Reminded" or back into a pressable "Try again", and
   * the reason for a failure is printed once above the table rather than
   * squeezed into a cell — it is a sentence, and the cell is a word wide.
   */
  const handleRemind = async (user: AdminUser) => {
    setReminders((prev) => ({ ...prev, [user.id]: "sending" }));
    setRemindError(null);

    try {
      await reactivateUser(user.id);
      setReminders((prev) => ({ ...prev, [user.id]: "sent" }));
    } catch (error) {
      setReminders((prev) => ({ ...prev, [user.id]: "failed" }));
      setRemindError(
        error instanceof Error
          ? error.message
          : "We could not send that reminder."
      );
    }
  };

  const handleAccountTypeChange = (type: AccountType) => {
    setAccountType(type);
    // A narrower list has fewer pages, and page 4 of it may not exist.
    setPage(1);
  };

  // No badge until the count is in: a "0" beside a tab that is still loading
  // reads as an answer rather than as a blank.
  //
  // Only All wears one. The roster endpoint's `tab_counts` splits the roster by
  // plan — all, free trial, pro — and this tab bar splits it by state, so the
  // only figure of the three that answers a tab is `all`. There is no count of
  // lapsed accounts on the wire to put beside Expired, and the number of rows
  // that tab happens to be showing is not it: that moves with the search box,
  // and a badge that moves with a filter is the pager's job, not a tab's.
  const tabs: TabItem<UserTabId>[] = (["all", "expired"] as UserTabId[]).map(
    (id) => ({
      id,
      label: TAB_COPY[id].label,
      count: id === "all" ? counts?.all : undefined,
    })
  );

  /** What the table shows in place of rows, whichever way it came to have none.
   *  The four ways are genuinely different and get genuinely different
   *  answers: a failed fetch, a search that was too narrow, a plan filter that
   *  matched nobody, and a roster that has nobody on it yet.
   *
   *  Nothing about loading here: while the list is out the table shimmers, and
   *  the status line in the card is what says so in words. */
  const emptyState = (): EmptyStateProps => {
    if (status === "failed") {
      return failedEmptyState(
        "We could not load the user list",
        <Button size="sm" variant="secondary" onClick={reload}>
          Try again
        </Button>
      );
    }

    if (query.trim()) return searchEmptyState("users");

    // A filter that matched nothing is not an empty roster, and the way out of
    // it is the filter rather than anything about the list.
    if (accountType !== "any") {
      return {
        title: "No users on that plan",
        description:
          "Nobody in this list is on the account type you picked. Clear the filter to see everyone again.",
        action: (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleAccountTypeChange("any")}
          >
            Clear filter
          </Button>
        ),
      };
    }

    if (activeTab === "expired") {
      return {
        title: "No expired accounts",
        description: "Nobody's plan has lapsed. Accounts appear here when one does.",
      };
    }

    return {
      variant: "welcome",
      illustration: <FirstUserMark aria-hidden className="w-11 h-11" />,
      title: "No users yet",
      description: "Accounts appear here as soon as people sign up to Naurist.",
    };
  };

  return (
    // The page owns the viewport height instead of growing past it, so the
    // only thing that scrolls is the table body inside the card.
    <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="Users" />

      <div className="mt-5 flex-shrink-0 border-b border-line">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
      </div>

      <section className="mt-5 flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
          <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
            {TAB_COPY[activeTab].cardTitle}
          </h2>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative w-full sm:w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search"
                aria-label="Search users"
                className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
              />
            </div>

            {/* Only on the full roster. On the Expired tab the list is already
                the answer to a narrower question, and a plan filter over two
                rows is a control that mostly empties the table. */}
            {activeTab === "all" && (
              <FilterDropdown<AccountType>
                placeholder="Account type"
                options={ACCOUNT_TYPE_OPTIONS}
                value={accountType}
                onChange={handleAccountTypeChange}
                defaultValue="any"
                className="w-full sm:w-[168px]"
              />
            )}
          </div>
        </div>

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the skeleton it stands for is hidden from screen
            readers — it is the shape of an answer rather than the answer. */}
        {status === "loading" && (
          <p role="status" className="sr-only">
            Loading users…
          </p>
        )}

        {/* Announced as well as printed: the press that caused it was in a
            cell that now says "Try again" and nothing else, so a reader who
            is not looking at the top of the card would otherwise be told only
            that the word changed. */}
        {remindError && (
          <p
            role="alert"
            className="flex-shrink-0 px-4 sm:px-6 pb-3 text-[13px] text-[#D14343]"
          >
            {remindError}
          </p>
        )}

        <UsersTable
          users={users}
          variant={activeTab === "expired" ? "expired" : "roster"}
          phase={phase}
          empty={emptyState()}
          onSelectUser={(user) => navigate(userDetailPath(user.id))}
          onRemind={handleRemind}
          remindStates={reminders}
        />

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          // The rows on screen, not the roster's total — the count beside the
          // pager describes the page it is paging.
          resultCount={status === "ready" ? users.length : undefined}
        />
      </section>
    </div>
  );
};

export default UsersPage;
